
import { defineComponent, reactive, onMounted, toRefs, h, ref, nextTick } from 'vue'
import { DoubleLeftOutlined, DoubleRightOutlined } from '@ant-design/icons-vue'
import f10ApiHk from '@/api/f10ApiHk'
import Table from '@/components/table.vue'
import Nodata from '@/components/page/nodata.vue'
import { useRoute } from 'vue-router'

export default defineComponent({
  props: {
    itemdata: {
      type: Object,
      default: () => ({})
    },
    token: {
      type: String,
      default: ''
    },
    theme: {
      type: String,
      default: 'light'
    }
  },
  components: { Table, Nodata, DoubleLeftOutlined, DoubleRightOutlined },

  setup(props) {
    const route = useRoute()
    const radioGroupRef = ref()
    const leftIconShow = ref(false)
    const rightIconShow = ref(false)

    const pageData = reactive({
      data: [] as any[],
      radiolist: [] as string[], // 时间单选项
      radiovalue: '',            // 当前选择的时间
      columns: [
        { title: '股东名称', dataIndex: 'holderName', key: 'holderName', align: 'left' },
        { title: '最终控制人', dataIndex: 'finalControlLayer', key: 'finalControlLayer', align: 'left' },
        { title: '占总股本比', dataIndex: 'formattedRatio', key: 'formattedRatio', align: 'right' },
        { title: '持股数', dataIndex: 'formattedShares', key: 'formattedShares', align: 'right' },
        {
          title: '持股变动',
          dataIndex: 'formattedChange',
          key: 'formattedChange',
          align: 'right',
          customRender: ({ record }: any) => {
            return h(
              'span',
              { style: { color: record.changeColor || 'inherit' } },
              record.formattedChange
            )
          }
        }
      ],
      spinning: true
    })
    const groupedData: Record<string, any[]> = {}

    onMounted(() => {
      const params = {
        data: {
          // market: 'HK',
          //  symbol: '00700' 
          market: route.query.market || 'HK',
          symbol: route.query.code || '00700',
        },
        headers: {
          token: props.token || '3d5eff0d3c7ac7f3af30c337dcdf6944'
        }
      }

      f10ApiHk.getBriefshareholderList(params).then((res: any) => {
        if (res.data && res.data.list) {
          const list = res.data.list

          // onMounted 数据处理部分
          list.forEach((item: any) => {
            const date = new Date(item.reportDate)
            const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1)
              .toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`

            if (!groupedData[formattedDate]) groupedData[formattedDate] = []

            // 占股比格式化
            let formattedRatio = ''
            if (item.longHeldRatio < 0.01) {
              formattedRatio = '<0.01%'
            } else {
              formattedRatio = item.longHeldRatio.toFixed(2) + '%'
            }

            // 持股变动格式化
            let formattedChange = '0'
            let changeColor = ''

            if (item.heldTotalChange == null) {
              formattedChange = '-'
              changeColor = '#aaaaaa'
            } else {
              const absValue = Math.abs(item.heldTotalChange)
              const sign = item.heldTotalChange > 0 ? '+' : ''

              if (absValue >= 100000000) {
                // 亿
                formattedChange = sign + (item.heldTotalChange / 100000000).toFixed(2) + '亿'
              } else if (absValue >= 10000) {
                // 万
                formattedChange = sign + (item.heldTotalChange / 10000).toFixed(2) + '万'
              } else {
                // 千（不进化）
                formattedChange = sign + item.heldTotalChange.toFixed(0)
              }

              changeColor = item.heldTotalChange > 0 ? 'red' : 'green'
            }

            groupedData[formattedDate].push({
              ...item,
              formattedShares: (item.heldTotalNumLongPosition / 100000000).toFixed(2) + '亿',
              formattedRatio,
              formattedChange,
              changeColor
            })
          })

          // 设置时间单选项，默认选最新日期
          pageData.radiolist = Object.keys(groupedData).sort((a, b) => (a < b ? 1 : -1))
          pageData.radiovalue = pageData.radiolist[0]
          pageData.data = groupedData[pageData.radiovalue]

          // 检查滚动状态
          nextTick(() => {
            checkScrollStatus()
            // 添加滚动事件监听器
            if (radioGroupRef.value) {
              const container = radioGroupRef.value.$el
              container.addEventListener('scroll', checkScrollStatus)
            }
          })
        }
        pageData.spinning = false
      }).catch(() => {
        pageData.spinning = false
      })
    })

    // 切换事件：直接用缓存的 groupedData
    const changeradio = (e: any) => {
      const selectedDate = e.target.value
      pageData.data = groupedData[selectedDate] || []
    }

    // 滚动到目标位置
    const scrollToTarget = (direction: 'left' | 'right') => {
      if (!radioGroupRef.value) return

      const container = radioGroupRef.value.$el
      const scrollAmount = 200 // 每次滚动的距离
      const { scrollLeft, scrollWidth, clientWidth } = container

      // 使用 requestAnimationFrame 确保平滑滚动
      requestAnimationFrame(() => {
        if (direction === 'left') {
          // 向左滚动，确保不会滚动到负数
          container.scrollLeft = Math.max(0, scrollLeft - scrollAmount)
        } else {
          // 向右滚动，确保不会超出最大滚动距离
          const maxScrollLeft = scrollWidth - clientWidth
          container.scrollLeft = Math.min(maxScrollLeft, scrollLeft + scrollAmount)
        }

        // 滚动后检查图标显示状态
        nextTick(() => {
          checkScrollStatus()
        })
      })
    }

    // 检查滚动状态，控制左右图标的显示
    const checkScrollStatus = () => {
      if (!radioGroupRef.value) return

      const container = radioGroupRef.value.$el
      const { scrollLeft, scrollWidth, clientWidth } = container

      leftIconShow.value = scrollLeft > 0
      // 当滚动到最右边时（允许1px的误差），隐藏右图标
      rightIconShow.value = scrollLeft < scrollWidth - clientWidth - 1
    }
    // 单选切换事件
    // const changeradio = (e: any) => {
    //   const selectedDate = e.target.value
    //   // 根据 radiovalue 过滤出对应数据
    //   f10ApiHk.getBriefshareholderList({
    //     data: { market: 'HK', symbol: '00700' },
    //     headers: { token: props.token || '3d5eff0d3c7ac7f3af30c337dcdf6944' }
    //   }).then((res: any) => {
    //     if (res.data && res.data.list) {
    //       const list = res.data.list.filter((item: any) => {
    //         const date = new Date(item.reportDate)
    //         const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1)
    //           .toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
    //         return formattedDate === selectedDate
    //       }).map((item: any) => ({
    //         ...item,
    //         formattedShares: (item.heldTotalNumLongPosition / 100000000).toFixed(2) + '亿股'
    //       }))
    //       pageData.data = list
    //     }
    //   })
    // }

    return {
      ...toRefs(pageData),
      radioGroupRef,
      leftIconShow,
      rightIconShow,
      changeradio,
      scrollToTarget
    }
  }
})
